import {
  useImportDashboards,
  getListDashboardsQueryKey,
  type DashboardsImportRequestFormat,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  FileJson,
  FileCode,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Home,
  X,
} from 'lucide-react';
import { useState, useCallback, useRef, useEffect } from 'react';
import YAML from 'yaml';

import { Button, Modal, Checkbox, useToast } from '@/components/ui';

export interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ImportFormat = DashboardsImportRequestFormat;

interface ParsedDashboard {
  name: string;
  description?: string;
  isShared?: boolean;
  isHome?: boolean;
  widgetCount: number;
}

interface ParseResult {
  format: ImportFormat;
  dashboards: ParsedDashboard[];
  warnings: string[];
  raw: string;
}

function detectFormat(content: string): ImportFormat | null {
  const trimmed = content.trim();
  // JSON starts with { or [
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      return null;
    }
  }
  // Try YAML
  try {
    const parsed = YAML.parse(trimmed);
    if (parsed && typeof parsed === 'object') {
      return 'yaml';
    }
  } catch {
    return null;
  }
  return null;
}

function parseContent(content: string): ParseResult | { error: string } {
  const format = detectFormat(content);
  if (!format) {
    return { error: 'Unable to detect format. Please ensure the file is valid JSON or YAML.' };
  }

  try {
    const parsed = format === 'json' ? JSON.parse(content) : YAML.parse(content);
    const warnings: string[] = [];
    let dashboards: ParsedDashboard[] = [];

    // Check for export envelope (multi-dashboard format)
    if (parsed['exportSchemaVersion'] && Array.isArray(parsed['dashboards'])) {
      dashboards = (parsed['dashboards'] as Record<string, unknown>[]).map(
        (d: Record<string, unknown>) => ({
          name: (d['name'] as string | undefined) || 'Unnamed Dashboard',
          description: d['description'] as string | undefined,
          isShared: d['isShared'] as boolean | undefined,
          isHome: d['isHome'] as boolean | undefined,
          widgetCount: Array.isArray((d['document'] as Record<string, unknown>)?.['widgets'])
            ? ((d['document'] as Record<string, unknown>)['widgets'] as unknown[]).length
            : 0,
        })
      );
    } else if (parsed['document'] && typeof parsed['document'] === 'object') {
      // Single dashboard format
      const doc = parsed['document'] as Record<string, unknown>;
      dashboards = [
        {
          name: (parsed['name'] as string | undefined) || 'Unnamed Dashboard',
          description: parsed['description'] as string | undefined,
          isShared: parsed['isShared'] as boolean | undefined,
          isHome: parsed['isHome'] as boolean | undefined,
          widgetCount: Array.isArray(doc['widgets']) ? (doc['widgets'] as unknown[]).length : 0,
        },
      ];
    } else {
      return {
        error: 'Invalid dashboard format. Expected dashboard or export envelope structure.',
      };
    }

    // Add warnings
    const homeCount = dashboards.filter((d) => d.isHome).length;
    if (homeCount > 1) {
      warnings.push(`Multiple dashboards (${homeCount}) are marked as home. Only one can be set.`);
    }

    const sharedCount = dashboards.filter((d) => d.isShared).length;
    if (sharedCount > 0) {
      warnings.push(
        `${sharedCount} dashboard(s) marked as shared will be imported as private for security.`
      );
    }

    return { format, dashboards, warnings, raw: content };
  } catch (err) {
    return {
      error: `Failed to parse ${format?.toUpperCase()}: ${err instanceof Error ? err.message : 'Unknown error'}`,
    };
  }
}

export function ImportModal({ isOpen, onClose }: ImportModalProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [content, setContent] = useState('');
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [setHomeOnImport, setSetHomeOnImport] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Parse content when it changes
  useEffect(() => {
    if (!content.trim()) {
      setParseResult(null);
      setParseError(null);
      return;
    }

    const result = parseContent(content);
    if ('error' in result) {
      setParseError(result.error);
      setParseResult(null);
    } else {
      setParseError(null);
      setParseResult(result);
    }
  }, [content]);

  const { mutate: importDashboards, isPending } = useImportDashboards({
    mutation: {
      onSuccess: (response) => {
        const count = response.dashboards?.length ?? 0;
        const warnings = response.warnings ?? [];

        if (warnings.length > 0) {
          addToast({
            message: `Imported ${count} dashboard(s) with ${warnings.length} warning(s)`,
            variant: 'warning',
          });
        } else {
          addToast({
            message: `Successfully imported ${count} dashboard(s)`,
            variant: 'success',
          });
        }

        queryClient.invalidateQueries({ queryKey: getListDashboardsQueryKey() });
        handleClose();
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to import dashboards',
          variant: 'error',
        });
      },
    },
  });

  const handleClose = useCallback(() => {
    setContent('');
    setParseResult(null);
    setParseError(null);
    setSetHomeOnImport(false);
    onClose();
  }, [onClose]);

  const handleFileSelect = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text === 'string') {
        setContent(text);
      }
    };
    reader.readAsText(file);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleImport = useCallback(() => {
    if (!parseResult) return;

    importDashboards({
      data: {
        content: parseResult.raw,
        format: parseResult.format,
        options: {
          setHome: setHomeOnImport,
        },
      },
    });
  }, [parseResult, setHomeOnImport, importDashboards]);

  const hasHomeInImport = parseResult?.dashboards.some((d) => d.isHome) ?? false;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Dashboards"
      subtitle="Import dashboards from JSON or YAML file"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleImport} loading={isPending} disabled={!parseResult}>
            <Upload className="mr-2 h-4 w-4" />
            Import {parseResult ? `(${parseResult.dashboards.length})` : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* File upload / paste area */}
        {!parseResult && (
          <>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                isDragging
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                  : 'border-gray-300 hover:border-gray-400 dark:border-white/[0.15] dark:hover:border-white/[0.25]'
              }`}
            >
              <div className="mb-4 flex gap-2">
                <FileJson className="h-8 w-8 text-gray-400 dark:text-zinc-500" />
                <FileCode className="h-8 w-8 text-gray-400 dark:text-zinc-500" />
              </div>
              <p className="mb-2 text-sm font-medium text-gray-700 dark:text-zinc-300">
                Drag and drop a file here, or
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.yaml,.yml"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                Browse Files
              </Button>
              <p className="mt-2 text-xs text-gray-500 dark:text-zinc-500">
                Supports JSON and YAML formats
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-gray-200 dark:border-white/[0.08]" />
              <p className="relative mx-auto w-fit bg-white px-3 text-sm text-gray-500 dark:bg-[#18181b] dark:text-zinc-500">
                or paste content below
              </p>
            </div>

            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste JSON or YAML content here..."
              className="h-40 w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white dark:placeholder:text-zinc-600"
            />
          </>
        )}

        {/* Parse error */}
        {parseError && (
          <div className="flex items-start gap-3 rounded-lg bg-red-50 p-3 dark:bg-red-500/10">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400" />
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-300">Parse Error</p>
              <p className="mt-0.5 text-sm text-red-600 dark:text-red-400">{parseError}</p>
            </div>
          </div>
        )}

        {/* Parse success - preview */}
        {parseResult && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Parsed successfully ({parseResult.format.toUpperCase()})
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setContent('');
                  setParseResult(null);
                }}
              >
                <X className="mr-1 h-4 w-4" />
                Clear
              </Button>
            </div>

            {/* Dashboard list */}
            <div className="rounded-lg border border-gray-200 dark:border-white/[0.08]">
              <div className="border-b border-gray-200 px-4 py-2 dark:border-white/[0.06]">
                <p className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                  {parseResult.dashboards.length} Dashboard
                  {parseResult.dashboards.length !== 1 ? 's' : ''} to Import
                </p>
              </div>
              <div className="max-h-48 divide-y divide-gray-100 overflow-y-auto dark:divide-white/[0.04]">
                {parseResult.dashboards.map((dashboard, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {dashboard.name}
                        </span>
                        {dashboard.isHome && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                            <Home className="h-3 w-3" />
                            Home
                          </span>
                        )}
                      </div>
                      {dashboard.description && (
                        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-zinc-500">
                          {dashboard.description}
                        </p>
                      )}
                    </div>
                    <span className="ml-4 text-xs text-gray-500 dark:text-zinc-500">
                      {dashboard.widgetCount} widget{dashboard.widgetCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Warnings */}
            {parseResult.warnings.length > 0 && (
              <div className="space-y-2">
                {parseResult.warnings.map((warning, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-500/10"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500 dark:text-amber-400" />
                    <p className="text-sm text-amber-700 dark:text-amber-300">{warning}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Set home option */}
            {hasHomeInImport && (
              <Checkbox
                label="Set home dashboard from import"
                description="If a dashboard is marked as home in the import, set it as your home dashboard"
                checked={setHomeOnImport}
                onChange={(e) => setSetHomeOnImport(e.target.checked)}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
